import { useCallback, useEffect, useMemo, useState } from "react";
import { Keyboard, ViewportFit } from "@eweadn-qiankun/shared-components";
import type { KeyPosition } from "../../Keyboard/types";
import CustomizeOperation from "../../../../../keyboard-management/src/pages/trigger/CustomizeOperation";
import type {
  CommunityPreviewDeviceMeta,
  FullBoardConfigData,
  SetPerformanceParType,
} from "@eweadn-qiankun/shared-types";
import {
  buildPreviewKeyboardLayoutWithChangeKey,
  buildTriggerPreviewDeviceContext,
  resolveCommunityPreviewKeyboardType,
} from "../utils/buildPreviewKeyboardLayout";
import {
  buildTriggerPreviewLayout,
  enrichTriggerSelectData,
  getTriggerPreviewParamsForKey,
  hasTriggerPreviewConfig,
  resolveTriggerMatrixPosition,
} from "../utils/boardConfigPreviewUtils";
import "../../../../../keyboard-management/src/pages/trigger/index.scss";

/** 与 keyboard-management trigger 页 getDefParams 一致（未选键时的占位回显） */
function getTriggerPreviewIdleParams(
  solutionProvider?: string
): SetPerformanceParType {
  const isHfdMagnetic = solutionProvider?.toLowerCase() === "huafenda";
  const defaultDeadStroke = isHfdMagnetic ? 0 : 0.2;
  return {
    row: 0,
    col: 0,
    mode: 0,
    normalPress: 1.5,
    normalRelease: 1.5,
    rtFirstTouch: 0.5,
    rtPress: 0.3,
    rtRelease: 0.3,
    pressDeadStroke: defaultDeadStroke,
    releaseDeadStroke: defaultDeadStroke,
    calibrate: 0,
    axis: 0,
    axisV2Id: 0,
    axisRangeMax: 0,
    axisCoefficient: 0,
  };
}

interface Props {
  meta: CommunityPreviewDeviceMeta;
  configData?: FullBoardConfigData;
  /** 0=Win，1=Mac */
  currentDeviceSystem?: number;
}

export default function KeyboardTriggerSection({
  meta,
  configData,
  currentDeviceSystem = 0,
}: Props) {
  const previewKeyboardType = useMemo(
    () => resolveCommunityPreviewKeyboardType(meta),
    [meta]
  );
  const triggerDeviceContext = useMemo(
    () => buildTriggerPreviewDeviceContext(meta, previewKeyboardType),
    [meta, previewKeyboardType]
  );
  const layout = useMemo(
    () =>
      buildPreviewKeyboardLayoutWithChangeKey(
        meta,
        configData,
        currentDeviceSystem
      ),
    [meta, configData, currentDeviceSystem]
  );

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectData, setSelectData] = useState<Record<string, KeyPosition>>({});

  useEffect(() => {
    setSelectedKeys([]);
    setSelectData({});
  }, [layout, configData]);

  const hasTriggerConfig = hasTriggerPreviewConfig(configData);

  const triggerLayoutWithPerformance = useMemo(
    () => buildTriggerPreviewLayout(layout, configData),
    [layout, configData]
  );

  const idleParams = useMemo(
    () => getTriggerPreviewIdleParams(meta.solutionProvider),
    [meta.solutionProvider]
  );

  const resolvedSelectData = useMemo(
    () => enrichTriggerSelectData(layout, selectedKeys, selectData),
    [layout, selectedKeys, selectData]
  );

  const params = useMemo(() => {
    if (!selectedKeys.length) return idleParams;
    const lastKey = selectedKeys[selectedKeys.length - 1];
    const pos = resolvedSelectData[lastKey];
    if (!pos) return idleParams;
    const { row, col } = resolveTriggerMatrixPosition(layout, lastKey, pos);
    const posLabel = resolvedSelectData[lastKey]?.label;
    return getTriggerPreviewParamsForKey(configData, row, col, posLabel);
  }, [configData, selectedKeys, resolvedSelectData, layout, idleParams]);

  const handleKeySelect = useCallback(
    (keys: string[], positions?: Record<string, KeyPosition>) => {
      setSelectedKeys(keys);
      setSelectData(enrichTriggerSelectData(layout, keys, positions ?? {}));
    },
    [layout]
  );

  /** 与子应用 getIsKeyData：有选键数据才可操作，否则控件置灰 */
  const isKeyData =
    selectData != null &&
    typeof selectData === "object" &&
    Object.keys(selectData).length > 0;

  return (
    <div className="community-kb-trigger-preview trigger">
      <div className="trigger-keyboard community-preview-interactive">
        <ViewportFit>
          <div className="trigger-keyboard-display">
            <Keyboard
              keyboardType={previewKeyboardType as never}
              layout={triggerLayoutWithPerformance}
              teampresetData={triggerLayoutWithPerformance}
              showTeampreset={hasTriggerConfig}
              styleMode="neutral"
              selectionMode="multiple"
              selectedKeys={selectedKeys}
              onKeySelect={handleKeySelect}
              showLabels
              enablePhysicalKeyboard={false}
              disableViewportFit
            />
          </div>
        </ViewportFit>
      </div>
      <div className="trigger-main">
        <CustomizeOperation
          params={params}
          setParams={() => undefined}
          axisList={[]}
          isKeyData={isKeyData}
          deviceContext={triggerDeviceContext}
        />
      </div>
    </div>
  );
}
